import React, { useCallback, useEffect, useRef, useState } from "react";
import { useCookies } from "react-cookie";
import { Link, useLocation, useNavigate } from "react-router-dom";
import logo from "../../assets/logos/logo.jpeg";
import contentApi from "../../services/contentApi";
import { canManageContent } from "../content-admin/contentAccess";
import "./Login.css";

const CONTENT_HOME = "/content-admin/blog";

const validToken = (value) => Boolean(value && value !== "undefined");

const safeLocalPath = (value) =>
  typeof value === "string" &&
  value.startsWith("/") &&
  !value.startsWith("//") &&
  !value.includes("\\");

const returnDestination = (requested) => {
  if (safeLocalPath(requested)) {
    return { path: requested, state: undefined, requested: true };
  }

  if (
    requested &&
    typeof requested === "object" &&
    safeLocalPath(requested.pathname)
  ) {
    const search =
      typeof requested.search === "string" && requested.search.startsWith("?")
        ? requested.search
        : "";
    return {
      path: `${requested.pathname}${search}`,
      state: requested.state,
      requested: true,
    };
  }

  return { path: "/", state: undefined, requested: false };
};

const cookieOptions = () => ({
  path: "/",
  sameSite: "lax",
  ...(typeof window !== "undefined" && window.location.protocol === "https:"
    ? { secure: true }
    : {}),
});

const Login = () => {
  const [cookies, setCookie, removeCookie] = useCookies(["mytoken"]);
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({
    username: "",
    email: "",
    password: "",
    firstName: "",
    lastName: "",
    phone: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const submittedToken = useRef(null);
  const destination = returnDestination(location.state?.from);
  const token = cookies.mytoken;
  const busy = status === "checking" || status === "submitting";
  const isLogin = mode === "login";

  const routeAuthenticatedUser = useCallback(
    async (verifiedToken, responseProfile) => {
      const profile = responseProfile || (await contentApi.getProfile(verifiedToken));
      const contentUser = canManageContent(profile);
      const asksForContent = destination.path.startsWith("/content-admin");
      const target = destination.requested
        ? asksForContent && !contentUser
          ? "/"
          : destination.path
        : contentUser
          ? CONTENT_HOME
          : "/";

      navigate(target, {
        replace: true,
        ...(destination.requested && target === destination.path && destination.state !== undefined
          ? { state: destination.state }
          : {}),
      });
    },
    [destination.path, destination.requested, destination.state, navigate]
  );

  useEffect(() => {
    if (!validToken(token) || submittedToken.current === token) return undefined;

    let current = true;
    setStatus("checking");
    setMessage("");
    routeAuthenticatedUser(token)
      .catch((error) => {
        if (!current) return;
        if (error?.status === 401) {
          setMessage("Your session has expired. Sign in again to continue.");
          setStatus("idle");
          removeCookie("mytoken", { path: "/" });
        } else {
          setMessage("We could not verify your account right now. Please sign in again.");
        }
      })
      .finally(() => {
        if (current) setStatus("idle");
      });

    return () => {
      current = false;
    };
  }, [removeCookie, routeAuthenticatedUser, token]);

  const update = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const validate = () => {
    if (!form.username.trim() || !form.password.trim()) {
      return "Username and password are required.";
    }
    if (!isLogin) {
      if (
        !form.email.trim() ||
        !form.firstName.trim() ||
        !form.lastName.trim() ||
        !form.phone.trim()
      ) {
        return "Complete every field to create your account.";
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
        return "Enter a valid email address.";
      }
      if (!/^\+?[\d\s().-]+$/.test(form.phone.trim())) {
        return "Enter a valid phone number.";
      }
    }
    return "";
  };

  const authenticate = async () => {
    const response = await contentApi.login({
      username: form.username.trim(),
      password: form.password,
    });
    if (!validToken(response?.token)) {
      throw new Error("The server did not return a login token.");
    }

    submittedToken.current = response.token;
    setCookie("mytoken", response.token, cookieOptions());
    await routeAuthenticatedUser(response.token, response.user || response.profile);
  };

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;

    const validationMessage = validate();
    if (validationMessage) {
      setMessage(validationMessage);
      return;
    }

    setMessage("");
    setStatus("submitting");
    try {
      if (!isLogin) {
        await contentApi.register({
          site: "insurance",
          username: form.username.trim(),
          email: form.email.trim(),
          password: form.password,
          first_name: form.firstName.trim(),
          last_name: form.lastName.trim(),
          phone: form.phone.trim(),
        });
      }
      await authenticate();
    } catch (error) {
      if (isLogin && (error?.status === 400 || error?.status === 401)) {
        setMessage("Invalid username or password.");
      } else if (!isLogin && error?.status === 400) {
        setMessage(error?.message || "That username or email is already in use.");
      } else {
        setMessage(
          error?.message ||
            (isLogin
              ? "Sign in is unavailable right now."
              : "We could not create your account right now.")
        );
      }
    } finally {
      setStatus("idle");
    }
  };

  const switchMode = () => {
    if (busy) return;
    setMode((current) => (current === "login" ? "register" : "login"));
    setMessage("");
    setShowPassword(false);
  };

  return (
    <main className="insurance-login-page">
      <section className="insurance-login-shell" aria-labelledby="insurance-login-title">
        <div className="insurance-login-brand">
          <Link className="insurance-login-logo" to="/" aria-label="Thrive Insurance home">
            <img src={logo} alt="" />
          </Link>
          <div>
            <span>Thrive Insurance</span>
            <h1 id="insurance-login-title">
              Protection starts with a clear conversation.
            </h1>
            <p>
              Access your Thrive account, or create one to stay connected with the
              insurance support you need.
            </p>
          </div>
          <ul aria-label="Account benefits">
            <li><i className="fas fa-check" aria-hidden="true" /> A simple, secure sign-in</li>
            <li><i className="fas fa-check" aria-hidden="true" /> One account for Thrive services</li>
            <li><i className="fas fa-check" aria-hidden="true" /> Dedicated access for content teams</li>
          </ul>
        </div>

        <div className="insurance-login-panel">
          <div className="insurance-login-heading">
            <span>{isLogin ? "Welcome back" : "Join Thrive"}</span>
            <h2>{isLogin ? "Sign in to your account" : "Create your account"}</h2>
            <p>
              {isLogin
                ? "Enter the username and password linked to your Thrive account."
                : "Tell us a little about yourself to get started."}
            </p>
          </div>

          {message && (
            <div className="insurance-login-alert" role="alert">
              <i className="fas fa-circle-exclamation" aria-hidden="true" />
              <span>{message}</span>
            </div>
          )}

          <form onSubmit={submit} noValidate>
            <label htmlFor="insurance-login-username">
              <span>Username</span>
              <div className="insurance-login-input">
                <i className="far fa-user" aria-hidden="true" />
                <input
                  id="insurance-login-username"
                  type="text"
                  value={form.username}
                  onChange={update("username")}
                  autoComplete="username"
                  disabled={busy}
                  autoFocus
                  required
                />
              </div>
            </label>

            {!isLogin && (
              <div className="insurance-login-grid">
                <label htmlFor="insurance-login-first-name">
                  <span>First name</span>
                  <input
                    id="insurance-login-first-name"
                    type="text"
                    value={form.firstName}
                    onChange={update("firstName")}
                    autoComplete="given-name"
                    disabled={busy}
                    required
                  />
                </label>
                <label htmlFor="insurance-login-last-name">
                  <span>Last name</span>
                  <input
                    id="insurance-login-last-name"
                    type="text"
                    value={form.lastName}
                    onChange={update("lastName")}
                    autoComplete="family-name"
                    disabled={busy}
                    required
                  />
                </label>
                <label htmlFor="insurance-login-email">
                  <span>Email address</span>
                  <input
                    id="insurance-login-email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    autoComplete="email"
                    disabled={busy}
                    required
                  />
                </label>
                <label htmlFor="insurance-login-phone">
                  <span>Phone number</span>
                  <input
                    id="insurance-login-phone"
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    autoComplete="tel"
                    disabled={busy}
                    required
                  />
                </label>
              </div>
            )}

            <div className="insurance-login-password-field">
              <label htmlFor="insurance-login-password"><span>Password</span></label>
              <div className="insurance-login-input insurance-login-password">
                <i className="fas fa-lock" aria-hidden="true" />
                <input
                  id="insurance-login-password"
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={update("password")}
                  autoComplete={isLogin ? "current-password" : "new-password"}
                  maxLength={isLogin ? undefined : 128}
                  aria-describedby={!isLogin ? "insurance-login-password-help" : undefined}
                  disabled={busy}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((current) => !current)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  disabled={busy}
                >
                  <i className={`far fa-eye${showPassword ? "-slash" : ""}`} aria-hidden="true" />
                </button>
              </div>
              {!isLogin && (
                <small id="insurance-login-password-help">
                  Choose any non-empty password up to 128 characters.
                </small>
              )}
            </div>

            <button className="insurance-login-submit" type="submit" disabled={busy}>
              {busy ? (
                <><span className="insurance-login-spinner" aria-hidden="true" /> Please wait...</>
              ) : isLogin ? (
                <>Sign in <i className="fas fa-arrow-right" aria-hidden="true" /></>
              ) : (
                <>Create account <i className="fas fa-arrow-right" aria-hidden="true" /></>
              )}
            </button>
          </form>

          <div className="insurance-login-switch">
            <span>{isLogin ? "New to Thrive Insurance?" : "Already have an account?"}</span>
            <button type="button" onClick={switchMode} disabled={busy}>
              {isLogin ? "Create an account" : "Sign in instead"}
            </button>
          </div>
          <Link className="insurance-login-back" to="/">Back to Thrive Insurance</Link>
        </div>
      </section>
    </main>
  );
};

export default Login;
